import { PlusCircleIcon } from 'lucide-react'
import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import type { FilterOption } from './data-table.interface'

interface DataTableFilterTriggerProps extends ComponentPropsWithoutRef<typeof Button> {
  label: string
  selectedOptionList: FilterOption[]
}

/**
 * Renders the trigger button for the filter dropdown with selected badges.
 * Uses forwardRef to support DropdownMenuTrigger's asChild prop.
 */
const DataTableFilterTriggerBase = forwardRef<HTMLButtonElement, DataTableFilterTriggerProps>(
  ({ label, selectedOptionList, className, ...props }, ref): ReactNode => (
    <Button
      ref={ref}
      variant="outline"
      size="sm"
      className={cn('data-table-filter-dropdown h-8 border-dashed', className)}
      {...props}
    >
      <PlusCircleIcon className="mr-2 size-4" />
      {label}
      {selectedOptionList.length > 0 && (
        <>
          <Separator orientation="vertical" className="mx-2 h-4" />
          <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
            {selectedOptionList.length}
          </Badge>
          <div className="hidden space-x-1 lg:flex">
            {selectedOptionList.length > 2 ? (
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selectedOptionList.length} selected
              </Badge>
            ) : (
              selectedOptionList.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="rounded-sm px-1 font-normal"
                >
                  {option.label}
                </Badge>
              ))
            )}
          </div>
        </>
      )}
    </Button>
  )
)

DataTableFilterTriggerBase.displayName = 'DataTableFilterTrigger'

export const DataTableFilterTrigger = memo(DataTableFilterTriggerBase)
